/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-misused-promises */

import { useState, useCallback, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIColumnWrapper from 'UIComponents/column/UIColumnWrapper';
import UIColumn from 'UIComponents/column/UIColumn';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIList from 'UIComponents/list/UIList';
import UIButton from 'UIComponents/button/UIButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import InvoicesEditorIFrameModal, { InvoiceOperation } from 'invoices-iframe-lib/components/InvoicesEditorIFrameModal';
import { addSuccess, addDanger, addWarning
// @ts-expect-error untyped customer-data-ui-utilities
} from 'customer-data-ui-utilities/alerts/Alerts';
import UICopyButton from 'UIComponents/button/UICopyButton';
import InvoiceDeleteModal from './InvoiceDeleteModal';
import { deleteInvoice, downloadInvoicePdf, patchInvoice } from '../api/invoiceAPI';
import InvoiceVoidModal from './InvoiceVoidModal';
import { FETCH_INVOICE } from '../graphQL/invoiceHighlightQuery';
import { InvoiceStatus } from 'invoices-ui-lib/constants/status';
import { getFinalizedInvoiceUrl, getFirstInvoiceAssociationWithRecordPage, hasPayments } from 'invoices-ui-lib/utils/invoice';
import { isFinalizedOrVoided } from 'invoices-ui-lib/utils/status';
import { getInvoiceProperty } from 'invoices-ui-lib/utils/property';
import { INVOICE_TYPE_ID, SUBSCRIPTION_TYPE_ID, COMMERCE_PAYMENT_TYPE_ID, COMPANY_TYPE_ID, CONTACT_TYPE_ID, LINE_ITEM_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { Map as ImmutableMap } from 'immutable';
import { trackInteraction, trackSendInteraction, trackSendUsage, trackUsage } from '../utils/tracking';
import useCanCreateInvoice from '../hooks/useCanCreateInvoice';
import UILink from 'UIComponents/link/UILink';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import InvoiceSendEmailModal, { rateLimitExceededErrorStatus } from 'invoices-ui-lib/components/InvoiceSendEmailModal/InvoiceSendEmailModal';
import useInvoiceCurrencyValidator from 'invoices-ui-lib/hooks/useInvoiceCurrencyValidator';
import RecordPaymentPanel from './recordPayment/RecordPaymentPanel';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { useFetchInvoiceProperties } from '../hooks/useFetchInvoiceProperties';
import { InvoicePaymentStatus } from 'invoices-ui-lib/types/InvoicePaymentStatus';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TooltipWithValidationMessages = ({
  children,
  disabledReasons,
  unknownValidationError
}) => {
  const disabled = !unknownValidationError && disabledReasons.length === 0;
  const headingText = useMemo(() => disabledReasons.length > 0 ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "invoicesHighlightCard.buttons.tooltips.cloneDisabled.headingText"
  }) : null, [disabledReasons]);
  const title = useMemo(() => {
    switch (true) {
      case unknownValidationError:
        {
          return /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.buttons.tooltips.unknownError.title"
          });
        }
      case disabledReasons.length > 0:
        return /*#__PURE__*/_jsx(UIList, {
          styled: true,
          children: disabledReasons
        });
      default:
        return null;
    }
  }, [unknownValidationError, disabledReasons]);
  return /*#__PURE__*/_jsx(UITooltip, {
    "data-test-id": "tooltip-with-validation-messages",
    disabled: disabled,
    headingText: headingText,
    title: title,
    children: children
  });
};
const InvoiceHighlightActions = props => {
  const {
    invoice,
    canEditInvoice,
    closeSidebar,
    onObjectUpdate,
    onObjectCreate,
    paymentStatus,
    updatePreview,
    canEditOpenInvoice,
    gates
  } = props;
  const sendInvoiceUpdatedMessage = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECT);
  const sendAssociationsUpdatedMessage = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_ASSOCIATIONS);
  const sendInvoiceDeletedMessage = useSendCrmMessageTopic(TOPIC_NAMES.OBJECT_DELETED);
  const [showInvoiceEditor, setShowInvoiceEditor] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showRecordPaymentPanel, setShowRecordPaymentPanel] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [voidInProgress, setVoidInProgress] = useState(false);
  const [operation, setOperation] = useState(null);
  const client = useApolloClient();
  const isPaymentCollectedAutomatically = useMemo(() => getInvoiceProperty(invoice, 'hs_invoice_payment_collection_method') === 'automatic', [invoice]);
  const associatedSubscription = useMemo(() => invoice.associatedObjects.INVOICE_TO_SUBSCRIPTION && invoice.associatedObjects.INVOICE_TO_SUBSCRIPTION[0], [invoice]);
  const {
    loading,
    disabledReasons,
    unknownValidationError
  } = useCanCreateInvoice(invoice);
  const {
    refetch
  } = useFetchInvoiceProperties(invoice.objectId, true);
  const invoiceStatus = getInvoiceProperty(invoice, 'hs_invoice_status');
  const invoiceBalance = getInvoiceProperty(invoice, 'hs_balance_due');
  const invoiceHasPayments = hasPayments(invoice) || paymentStatus === InvoicePaymentStatus.PENDING;
  const {
    loading: currencyValidatorLoading,
    error: currencyValidatorError,
    currencyValidator
  } = useInvoiceCurrencyValidator(![InvoiceStatus.DRAFT, InvoiceStatus.OPEN].includes(invoiceStatus));
  const maybeCurrencyValidationError = useMemo(() => currencyValidator(true, getInvoiceProperty(invoice, 'hs_currency')), [currencyValidator, invoice]);
  const disableClone = loading || disabledReasons.length > 0 || unknownValidationError;
  const disableEdit = currencyValidatorLoading || currencyValidatorError || !!maybeCurrencyValidationError || invoiceHasPayments;
  const invoiceLink = getFinalizedInvoiceUrl(invoice);
  const associatedObjectToLink = useMemo(() => getFirstInvoiceAssociationWithRecordPage(invoice), [invoice]);
  const refetchHighlightQueries = useCallback(() => {
    client.refetchQueries({
      include: [FETCH_INVOICE]
    }).catch(() => {
      addDanger('invoicesHighlightCard.alerts.generic.danger');
    });
  }, [client]);
  const updateInvoiceSidebarCard = useCallback(() => sendInvoiceUpdatedMessage({
    objectTypeId: INVOICE_TYPE_ID,
    objectId: String(invoice.objectId)
  }), [invoice.objectId, sendInvoiceUpdatedMessage]);
  const updateInvoiceAssociations = useCallback(toObjectTypeId => sendAssociationsUpdatedMessage({
    objectTypeId: INVOICE_TYPE_ID,
    toObjectTypeId,
    objectId: String(invoice.objectId)
  }), [invoice.objectId, sendAssociationsUpdatedMessage]);
  const updateInvoiceIndexTable = useCallback(updatedInvoicePropertiesAfterPayment => {
    const properties = {
      hs_invoice_status: updatedInvoicePropertiesAfterPayment.data.crmObject.defaultProperties.hs_invoice_status.value,
      hs_balance_due: updatedInvoicePropertiesAfterPayment.data.crmObject.defaultProperties.hs_balance_due.value,
      hs_payment_date: updatedInvoicePropertiesAfterPayment.data.crmObject.defaultProperties.hs_payment_date.value
    };
    onObjectUpdate({
      objectType: INVOICE_TYPE_ID,
      objectId: String(invoice.objectId),
      properties: ImmutableMap(properties)
    });
  }, [invoice.objectId, onObjectUpdate]);
  const maybeUpdatePaymentIndexTable = useCallback(objectId => {
    if (onObjectCreate) {
      onObjectCreate({
        associationType: COMMERCE_PAYMENT_TYPE_ID,
        objectId: String(objectId)
      });
    }
  }, [onObjectCreate]);
  const handleEditButtonClick = useCallback(() => {
    setOperation(InvoiceOperation.EDIT);
    setShowInvoiceEditor(true);
  }, []);
  const handleInvoiceEditorClose = useCallback(updatedInvoice => {
    setShowInvoiceEditor(false);
    updateInvoiceSidebarCard();
    updateInvoiceAssociations(COMPANY_TYPE_ID);
    updateInvoiceAssociations(LINE_ITEM_TYPE_ID);
    updateInvoiceAssociations(CONTACT_TYPE_ID);
    client.refetchQueries({
      include: [FETCH_INVOICE]
    }).catch(() => {
      addDanger('invoicesHighlightCard.alerts.generic.danger');
    });
    if (updatedInvoice) {
      if (operation === InvoiceOperation.CLONE && onObjectCreate) {
        onObjectCreate({
          associationType: INVOICE_TYPE_ID,
          objectId: String(updatedInvoice.objectId)
        });
      } else {
        onObjectUpdate({
          objectType: INVOICE_TYPE_ID,
          objectId: String(updatedInvoice.objectId),
          properties: Object.keys(updatedInvoice.properties).reduce((acc, prop) => {
            const property = updatedInvoice.properties[prop];
            return acc.set(property.name, property.value);
          }, ImmutableMap())
        });
      }
    }
  }, [client, updateInvoiceSidebarCard, onObjectUpdate, onObjectCreate, operation, updateInvoiceAssociations]);
  const handleDeleteButtonClick = useCallback(() => {
    setShowDeleteModal(true);
  }, []);
  const handleDeleteModalConfirm = useCallback(() => {
    trackUsage({
      action: 'Click delete invoice'
    });
    setDeleteInProgress(true);
    deleteInvoice(invoice.objectId).then(() => {
      return new Promise(resolve => setTimeout(() => {
        updateInvoiceSidebarCard();
        // The order of the next 2 lines cannot change
        // The index page only subscribes to the object deleted message when the preview sidebar is open
        // so we need to send that first
        sendInvoiceDeletedMessage({
          objectTypeId: INVOICE_TYPE_ID,
          objectId: String(invoice.objectId)
        });
        closeSidebar();
        addSuccess('invoicesHighlightCard.alerts.delete.success', {}, {
          'data-test-id': 'delete-invoice-success'
        });
        resolve();
      }, 3000));
    }).catch(() => {
      addDanger('invoicesHighlightCard.alerts.delete.danger');
    }).finally(() => {
      setDeleteInProgress(false);
      setShowDeleteModal(false);
    });
  }, [closeSidebar, invoice, updateInvoiceSidebarCard, sendInvoiceDeletedMessage]);
  const handleDeleteModalReject = useCallback(() => {
    setShowDeleteModal(false);
  }, []);
  const handleVoidButtonClick = useCallback(() => {
    setShowVoidModal(true);
  }, []);
  const handleCloneButtonClick = useCallback(() => {
    trackUsage({
      action: 'Click clone invoice'
    });
    setOperation(InvoiceOperation.CLONE);
    setShowInvoiceEditor(true);
  }, []);
  const handleVoidModalConfirm = useCallback(() => {
    trackUsage({
      action: 'Click void invoice'
    });
    setVoidInProgress(true);
    patchInvoice(invoice.objectId, [{
      name: 'hs_invoice_status',
      value: InvoiceStatus.VOIDED
    }]).then(() => {
      addSuccess('invoicesHighlightCard.alerts.void.success', {}, {
        'data-test-id': 'void-invoice-success'
      });
      client.refetchQueries({
        include: [FETCH_INVOICE]
      }).catch(() => {
        addDanger('invoicesHighlightCard.alerts.generic.danger');
      });
      updateInvoiceSidebarCard();
      onObjectUpdate({
        objectType: INVOICE_TYPE_ID,
        objectId: String(invoice.objectId),
        properties: ImmutableMap({
          hs_invoice_status: InvoiceStatus.VOIDED
        })
      });
    }).catch(() => {
      addDanger('invoicesHighlightCard.alerts.void.danger');
    }).finally(() => {
      setVoidInProgress(false);
      setShowVoidModal(false);
    });
  }, [client, invoice.objectId, updateInvoiceSidebarCard, onObjectUpdate]);
  const handleVoidModalReject = useCallback(() => {
    setShowVoidModal(false);
  }, []);
  const handleSendEmailModalOnClose = ({
    success,
    error,
    fatalError
  }) => {
    if (success) {
      addSuccess('invoicesHighlightCard.alerts.sendEmail.success.message', {}, {
        'data-test-id': 'highlight-send-invoice-success',
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.alerts.sendEmail.success.titleText"
        })
      });
    } else if (error || fatalError) {
      if (error && error.status === rateLimitExceededErrorStatus) {
        addDanger('invoicesHighlightCard.alerts.sendEmail.rateLimitExceededError.message', {}, {
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.alerts.sendEmail.rateLimitExceededError.titleText"
          })
        });
      } else {
        addDanger('invoicesHighlightCard.alerts.sendEmail.error.message', {}, {
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.alerts.sendEmail.error.titleText"
          })
        });
      }
    }
    setShowSendEmailModal(false);
  };
  const handleDownloadButtonClick = useCallback(() => {
    trackUsage({
      action: 'Click download invoice'
    });
    downloadInvoicePdf(invoice.objectId).then(response => window.location.href = response.url).catch(error => {
      if (error.status === 404) {
        addWarning('invoicesHighlightCard.alerts.download.warning');
      } else {
        throw error;
      }
    });
  }, [invoice.objectId]);
  const pollForInvoiceUpdates = useCallback(async () => {
    const MAX_RETRIES = 10;
    let counter = 0;
    let updatedInvoiceBalanceDue = invoiceBalance;
    let refetchedInvoice = null;
    while (counter <= MAX_RETRIES && invoiceBalance === updatedInvoiceBalanceDue) {
      if (counter > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      try {
        refetchedInvoice = await refetch();
        updatedInvoiceBalanceDue = refetchedInvoice.data.crmObject.defaultProperties.hs_balance_due.value;
        counter++;
      } catch (e) {
        counter++;
      }
    }
    return invoiceBalance !== updatedInvoiceBalanceDue ? refetchedInvoice : null;
  }, [refetch, invoiceBalance]);
  const handleRecordPaymentOnAdd = useCallback(async numberAdded => {
    FloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoicesHighlightCard.alerts.recordPayment.successAdd",
        options: {
          count: numberAdded
        }
      }),
      type: 'success'
    });
    trackUsage({
      action: 'Add existing payment'
    });
    const updatedInvoice = await pollForInvoiceUpdates();
    if (updatedInvoice) {
      refetchHighlightQueries();
      updateInvoiceIndexTable(updatedInvoice);
      setShowRecordPaymentPanel(false);
      updateInvoiceAssociations(COMMERCE_PAYMENT_TYPE_ID);
    } else {
      closeSidebar();
      setShowRecordPaymentPanel(false);
    }
  }, [closeSidebar, pollForInvoiceUpdates, refetchHighlightQueries, updateInvoiceIndexTable, updateInvoiceAssociations]);
  const handleRecordPaymentOnCreate = useCallback(async commercePaymentObject => {
    FloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "invoicesHighlightCard.alerts.recordPayment.successNew_jsx",
        elements: {
          UILink
        },
        options: {
          onClick: () => updatePreview(COMMERCE_PAYMENT_TYPE_ID, String(commercePaymentObject.objectId))
        }
      }),
      type: 'success'
    });
    trackUsage({
      action: 'Add new payment'
    });
    const updatedInvoice = await pollForInvoiceUpdates();
    if (updatedInvoice) {
      refetchHighlightQueries();
      updateInvoiceIndexTable(updatedInvoice);
      maybeUpdatePaymentIndexTable(commercePaymentObject.objectId);
      setShowRecordPaymentPanel(false);
      updateInvoiceAssociations(COMMERCE_PAYMENT_TYPE_ID);
    } else {
      closeSidebar();
      setShowRecordPaymentPanel(false);
    }
  }, [closeSidebar, maybeUpdatePaymentIndexTable, pollForInvoiceUpdates, refetchHighlightQueries, updateInvoiceIndexTable, updatePreview, updateInvoiceAssociations]);
  const editButton = useMemo(() => {
    if ((invoiceStatus === InvoiceStatus.DRAFT || invoiceStatus === InvoiceStatus.OPEN && canEditOpenInvoice) && canEditInvoice) {
      const disabled = disableEdit || isPaymentCollectedAutomatically || associatedSubscription && invoiceStatus === InvoiceStatus.OPEN;
      const tooltipMessage = invoiceHasPayments ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoicesHighlightCard.buttons.tooltips.editDisabled.hasAssociatedPayment"
      }) : isPaymentCollectedAutomatically || associatedSubscription ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoicesHighlightCard.buttons.tooltips.editDisabled.hasAssociatedSubscription"
      }) : currencyValidatorError ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoicesHighlightCard.buttons.tooltips.editDisabled.unknownError"
      }) : maybeCurrencyValidationError;
      return /*#__PURE__*/_jsx(UITooltip, {
        "data-test-id": "edit-btn-tooltip",
        disabled: !disabled,
        title: tooltipMessage,
        children: /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "edit-btn",
          use: "tertiary-light",
          responsive: false,
          size: "extra-small",
          onClick: handleEditButtonClick,
          disabled: disabled,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.buttons.edit"
          })
        }, "edit-btn")
      }, "edit-btn-tooltip");
    }
    return null;
  }, [canEditInvoice, maybeCurrencyValidationError, handleEditButtonClick, invoiceStatus, disableEdit, currencyValidatorError, canEditOpenInvoice, invoiceHasPayments, associatedSubscription, isPaymentCollectedAutomatically]);
  const deleteButton = useMemo(() => {
    if (invoiceStatus === InvoiceStatus.DRAFT && canEditInvoice && !isPaymentCollectedAutomatically && !associatedSubscription) {
      return /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "delete-btn",
        use: "tertiary-light",
        responsive: false,
        size: "extra-small",
        onClick: handleDeleteButtonClick,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.buttons.delete"
        })
      }, "delete-btn");
    }
    return null;
  }, [canEditInvoice, invoiceStatus, handleDeleteButtonClick, associatedSubscription, isPaymentCollectedAutomatically]);
  const viewButton = useMemo(() => {
    if (isFinalizedOrVoided(invoice)) {
      return /*#__PURE__*/_jsx(UILink, {
        onClick: () => {
          trackUsage({
            action: 'Click view invoice'
          });
        },
        "data-test-id": "view-btn",
        external: true,
        href: invoiceLink,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.buttons.view"
        })
      }, "view-btn");
    }
    return null;
  }, [invoice, invoiceLink]);
  const copyLinkButton = useMemo(() => {
    if (isFinalizedOrVoided(invoice)) {
      return /*#__PURE__*/_jsx(UICopyButton, {
        onClick: () => {
          trackInteraction({
            action: 'Click copy link'
          });
        },
        "data-test-id": "copy-link-btn",
        responsive: false,
        size: "extra-small",
        value: invoiceLink,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.buttons.copyLink"
        })
      }, "copy-link-btn");
    }
    return null;
  }, [invoice, invoiceLink]);
  const sendButton = useMemo(() => {
    if (isFinalizedOrVoided(invoice)) {
      return /*#__PURE__*/_jsx(UITooltip, {
        "data-test-id": "send-btn-tooltip",
        disabled: !!associatedObjectToLink,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.buttons.tooltips.pending.send"
        }),
        children: /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "send-btn",
          use: "tertiary-light",
          responsive: false,
          size: "extra-small",
          onClick: () => {
            trackUsage({
              action: 'Click send invoice'
            });
            setShowSendEmailModal(true);
          },
          disabled: !associatedObjectToLink,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.buttons.send"
          })
        })
      }, "send-btn");
    }
    return null;
  }, [invoice, associatedObjectToLink]);
  const downloadButton = useMemo(() => {
    if (isFinalizedOrVoided(invoice)) {
      return /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "download-btn",
        use: "tertiary-light",
        responsive: false,
        size: "extra-small",
        onClick: handleDownloadButtonClick,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoicesHighlightCard.buttons.download"
        })
      }, "download-btn");
    }
    return null;
  }, [handleDownloadButtonClick, invoice]);
  const recordPaymentPanel = useMemo(() => showRecordPaymentPanel ? /*#__PURE__*/_jsx(RecordPaymentPanel, {
    invoice: invoice,
    onReject: () => {
      setShowRecordPaymentPanel(false);
    },
    onFailure: error => {
      var _error$responseJSON;
      const OUT_OF_DATE_ERROR = 'ExternalPaymentValidation.INVOICE_OUT_OF_DATE';
      if ((error === null || error === void 0 || (_error$responseJSON = error.responseJSON) === null || _error$responseJSON === void 0 ? void 0 : _error$responseJSON.subCategory) === OUT_OF_DATE_ERROR) {
        addDanger('invoicesHighlightCard.alerts.recordPayment.dangerOutdatedInvoice');
      } else {
        addDanger('invoicesHighlightCard.alerts.recordPayment.danger');
      }
      setShowRecordPaymentPanel(false);
    },
    onAdd: handleRecordPaymentOnAdd,
    onCreate: handleRecordPaymentOnCreate
  }) : null, [showRecordPaymentPanel, invoice, handleRecordPaymentOnAdd, handleRecordPaymentOnCreate]);
  const handleRecordPaymentButtonClick = useCallback(() => {
    setShowRecordPaymentPanel(true);
  }, []);
  const recordPaymentButton = useMemo(() => {
    const isBalancePositive = Number(getInvoiceProperty(invoice, 'hs_balance_due')) > 0;
    if (invoiceStatus === InvoiceStatus.OPEN && isBalancePositive && canEditInvoice) {
      const tooltipTitle = isPaymentCollectedAutomatically ? associatedSubscription ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "invoicesHighlightCard.buttons.tooltips.subscriptionInvoice.recordPayment_jsx",
        elements: {
          UILink
        },
        options: {
          onClick: () => associatedSubscription.objectId && updatePreview(SUBSCRIPTION_TYPE_ID, associatedSubscription.objectId.toString())
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoicesHighlightCard.buttons.tooltips.subscriptionInvoice.recordPaymentNoSubscription"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoicesHighlightCard.buttons.tooltips.pending.recordPayment"
      });
      return /*#__PURE__*/_jsx(UITooltip, {
        disabled: !isPaymentCollectedAutomatically,
        title: tooltipTitle,
        children: /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "record-payment-btn",
          use: "tertiary-light",
          responsive: false,
          size: "extra-small",
          onClick: handleRecordPaymentButtonClick,
          disabled: isPaymentCollectedAutomatically,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.buttons.recordPayment"
          })
        }, "record-payment-btn")
      }, "record-payment-btn");
    }
    return null;
  }, [canEditInvoice, invoice, invoiceStatus, handleRecordPaymentButtonClick, updatePreview, isPaymentCollectedAutomatically, associatedSubscription]);
  const voidButton = useMemo(() => {
    if (invoiceStatus === InvoiceStatus.OPEN && canEditInvoice) {
      const disabled = invoiceHasPayments || isPaymentCollectedAutomatically;
      const tooltipTitle = isPaymentCollectedAutomatically ? associatedSubscription ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "invoicesHighlightCard.buttons.tooltips.subscriptionInvoice.void_jsx",
        elements: {
          UILink
        },
        options: {
          onClick: () => invoice.associatedObjects.INVOICE_TO_SUBSCRIPTION[0].objectId && updatePreview(SUBSCRIPTION_TYPE_ID, invoice.associatedObjects.INVOICE_TO_SUBSCRIPTION[0].objectId.toString())
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoicesHighlightCard.buttons.tooltips.subscriptionInvoice.voidNoSubscription"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "invoicesHighlightCard.buttons.tooltips.pending.void"
      });
      return /*#__PURE__*/_jsx(UITooltip, {
        disabled: !disabled,
        title: tooltipTitle,
        children: /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "void-btn",
          use: "tertiary-light",
          responsive: false,
          size: "extra-small",
          disabled: disabled,
          onClick: handleVoidButtonClick,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.buttons.void"
          })
        })
      }, "void-btn");
    }
    return null;
  }, [canEditInvoice, handleVoidButtonClick, invoiceStatus, invoice, updatePreview, invoiceHasPayments, isPaymentCollectedAutomatically, associatedSubscription]);
  const cloneButton = useMemo(() => {
    if (canEditInvoice && !(isPaymentCollectedAutomatically && invoiceStatus === InvoiceStatus.DRAFT)) {
      return /*#__PURE__*/_jsx(TooltipWithValidationMessages, {
        disabledReasons: disabledReasons,
        unknownValidationError: unknownValidationError,
        children: /*#__PURE__*/_jsx(UIButton, {
          onClick: handleCloneButtonClick,
          "data-test-id": "clone-btn",
          disabled: disableClone,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "invoicesHighlightCard.buttons.clone"
          })
        })
      }, "clone-btn-tooltip");
    }
    return null;
  }, [canEditInvoice, disableClone, disabledReasons, handleCloneButtonClick, unknownValidationError, invoiceStatus, isPaymentCollectedAutomatically]);
  const renderInvoiceEditor = () => {
    if (!canEditInvoice) return null;
    return /*#__PURE__*/_jsx(InvoicesEditorIFrameModal, {
      show: showInvoiceEditor,
      objectId: String(invoice.objectId),
      onClose: handleInvoiceEditorClose,
      objectTypeId: INVOICE_TYPE_ID,
      operation: operation
    });
  };
  const renderDeleteModal = () => {
    if (!showDeleteModal) {
      return null;
    }
    return /*#__PURE__*/_jsx(InvoiceDeleteModal, {
      invoice: invoice,
      onConfirm: handleDeleteModalConfirm,
      onReject: handleDeleteModalReject,
      deleteInProgress: deleteInProgress
    });
  };
  const renderVoidModal = () => {
    if (!showVoidModal) {
      return null;
    }
    return /*#__PURE__*/_jsx(InvoiceVoidModal, {
      onConfirm: handleVoidModalConfirm,
      onReject: handleVoidModalReject,
      voidInProgress: voidInProgress
    });
  };
  const renderSendEmailModal = () => {
    if (!showSendEmailModal) {
      return null;
    }
    return /*#__PURE__*/_jsx(InvoiceSendEmailModal, {
      invoice: invoice,
      onClose: handleSendEmailModalOnClose,
      onInteraction: trackSendInteraction,
      onUsage: trackSendUsage,
      gates: gates
    });
  };
  const renderModals = () => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderInvoiceEditor(), renderDeleteModal(), recordPaymentPanel, renderVoidModal(), renderSendEmailModal()]
    });
  };
  const availableActions = [editButton, deleteButton, viewButton, copyLinkButton, sendButton, downloadButton, recordPaymentButton, voidButton, cloneButton].filter(action => action !== null);
  if (availableActions.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      className: "display-flex",
      children: /*#__PURE__*/_jsxs(UIColumnWrapper, {
        align: "center",
        children: [/*#__PURE__*/_jsx(UIColumn, {}), /*#__PURE__*/_jsx(UIColumn, {
          children: /*#__PURE__*/_jsx(UIDropdown, {
            "data-test-id": "invoice-highlight-actions-dropdown",
            menuWidth: 200,
            placement: "bottom right",
            buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "invoicesHighlightCard.buttons.actions"
            }),
            buttonUse: "transparent",
            children: /*#__PURE__*/_jsx(UIList, {
              children: availableActions
            })
          })
        })]
      })
    }), renderModals()]
  });
};
export default InvoiceHighlightActions;